import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

// — Casi di riferimento (snapshot, link diretti)
const REFERENCE_CASES = [
  {
    to: "/works/ac-milan-connect",
    chip: "Business Impact · Strategy & Performance",
    title: "AC Milan Connect",
    kpi: "1° club al mondo con SIM dedicata · 2.774 pre-iscritti in 48 ore",
    image:
      "/images/assets/gsvgr6p9_1698141384711.jpg",
  },
  {
    to: "/works/credem-extra-ordinary",
    chip: "Social Impact · CSR strutturata",
    title: "Credem Banca × Extra Ordinary",
    kpi: "Da title sponsor a programma editoriale strutturato",
    image:
      "/images/assets/3pb7sube_20221017_15-28-55_z62_9552-980x653.jpg",
  },
  {
    to: "/works/volleyball-world-italia",
    chip: "Cultural Impact · Community Building",
    title: "Volleyball World Italia",
    kpi: "Da zero a 121K follower in 24 mesi",
    image:
      "/images/assets/3qbk4db2_2.png",
  },
];

const METHOD_STEPS = [
  {
    num: "01",
    title: "Discovery",
    body:
      "Sessione di lavoro intensiva con il C-level e il team marketing del cliente. Mappiamo gli investimenti sportivi attuali, gli obiettivi dichiarati, i risultati misurati (e quelli ignorati). Identifichiamo dove c'è valore non realizzato e dove c'è dispersione.",
  },
  {
    num: "02",
    title: "Posizionamento",
    body:
      "Costruiamo il posizionamento sport del brand a partire da quello generale. Definiamo i territori narrativi che il brand può presidiare in modo credibile e differenziato, e quelli che invece deve evitare.",
  },
  {
    num: "03",
    title: "Business case",
    body:
      "Documento di sintesi pensato per il board: ipotesi di investimento, KPI attesi, framework di misurazione tridimensionale, scenario di rischio. È il pezzo che il CMO porta al CFO per ottenere il budget.",
  },
  {
    num: "04",
    title: "Roadmap operativa",
    body:
      "Piano trimestrale con attività, partner coinvolti, deliverable, deadline e KPI di check-point. Da qui in poi il cliente può decidere se eseguire internamente o se proseguire con OnSports.",
  },
  {
    num: "05",
    title: "Governance & presidio",
    body:
      "Setup dei processi di monitoraggio interni: cadenza di review, dashboard, ruoli decisionali. La strategia smette di vivere \"nella testa del marketing manager\" e diventa un'infrastruttura aziendale.",
  },
];

const DELIVERABLES = [
  { output: "Audit del portafoglio sportivo attuale", format: "PDF + dashboard", to: "Marketing + CFO" },
  { output: "Posizionamento sport del brand", format: "Brand book sport-specifico", to: "Tutto il management" },
  { output: "Business case per il board", format: "Slide deck + executive summary", to: "C-level + Board" },
  { output: "Roadmap strategica trimestrale", format: "Documento operativo", to: "Marketing operations" },
  { output: "Framework di misurazione 3D", format: "Scorecard + KPI tree", to: "Marketing + Finance" },
  { output: "Workshop con il team interno", format: "2-4 sessioni", to: "Marketing + comunicazione" },
  { output: "Advisory continuativa", format: "Call mensili + accesso diretto", to: "CMO / Board" },
];

const PROFILES = [
  {
    title: "Brand corporate con sponsorship attive",
    body:
      "Aziende che hanno già più sponsorship in portfolio (sport, cultura, eventi) e vogliono razionalizzare il portafoglio, alzare il ROI medio e costruire un framework di misurazione che resista nel tempo.",
  },
  {
    title: "Property sportive in cerca di posizionamento",
    body:
      "Club, federazioni, leghe e organizzatori di eventi che hanno bisogno di costruire la propria proposta commerciale verso brand sponsor con un linguaggio più moderno e KPI più solidi.",
  },
  {
    title: "Aziende che entrano nello sport per la prima volta",
    body:
      "Brand consumer e B2B che non hanno mai investito strutturalmente nello sport e vogliono farlo nel modo giusto: posizionamento, framework, primo investimento pilota.",
  },
];

const KPI_3D = [
  {
    dim: "Business Impact",
    kpi: "Lead qualificati, CAC, revenue attribuibile, ROAS, conversion rate, AOV",
    tools: "Analytics, CRM, attribution model",
  },
  {
    dim: "Cultural Impact",
    kpi: "Brand salience qualificata, share of voice tematico, adozione lessico brand",
    tools: "Brand tracking, social listening qualitativo",
  },
  {
    dim: "Social Impact",
    kpi: "Beneficiari diretti, indicatori SDG, riportabilità ESG",
    tools: "Audit indipendente, framework GRI",
  },
];

const FAQS = [
  {
    q: "Quanto dura un progetto di Impact Strategy?",
    a:
      "Dipende dal formato. Un assessment puntuale richiede 4-6 settimane. Una roadmap strategica completa richiede 8-12 settimane. L'advisory continuativa è progettata per durare 12 mesi rinnovabili.",
  },
  {
    q: "Lavorate solo con grandi aziende?",
    a:
      "No. Lavoriamo con qualunque organizzazione che abbia almeno un investimento sportivo strutturato (sponsorship, partnership, programma di brand activation). La nostra logica vale per una PMI con un singolo sponsor come per un gruppo multinazionale con un portafoglio di partnership.",
  },
  {
    q: "Eseguite anche la strategia che proponete?",
    a:
      "Spesso sì, ma non sempre. L'Impact Strategy è progettata per essere autonoma: produce documenti e framework che il cliente può eseguire internamente o con altri fornitori. Quando il cliente lo richiede, gestiamo anche l'esecuzione attraverso gli altri servizi del listino (Sponsorship Programs, Content & IP Studio, Performance Marketing).",
  },
  {
    q: "Cosa vuol dire framework di misurazione tridimensionale?",
    a:
      "Significa che misuriamo gli investimenti sportivi su tre piani contemporaneamente — Business, Cultural, Social Impact — anziché solo su uno (di solito Business o awareness pura). Questo permette al cliente di difendere l'investimento sia con la finance (Business) sia con la comunicazione (Cultural) sia con il reporting ESG (Social).",
  },
  {
    q: "Lavorate con property sportive o solo con brand?",
    a:
      "Entrambi. Il framework funziona simmetricamente: dal lato brand (chi investe), dal lato property (chi vende le proprie sponsorship). Spesso lavoriamo su entrambi i lati di una stessa partnership.",
  },
];

const ServizioImpactStrategy = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "Impact Strategy & Advisory", url: "/servizi/impact-strategy-advisory" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "Impact Strategy & Advisory",
    description: "Consulenza strategica per brand sportivi: positioning, narrativa, KPI di impatto.",
    slug: "impact-strategy-advisory",
  });

  return (
    <>
      <Seo
        title="Impact Strategy & Advisory: consulenza brand sportivi | OnSports"
        description="Consulenza strategica per brand sportivi e property. Definiamo positioning, narrativa, KPI di impatto su Business, Cultural e Social. OnSports a Milano dal 2018."
        path="/servizi/impact-strategy-advisory"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-impact-strategy"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="01" className="mt-8">
              Impact Strategy &amp; Advisory
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Consulenza · Strategia · Board-level
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">Strategia</span>
              <span className="block">che convince</span>
              <span className="block">
                il <span className="text-lime">BOARD.</span>
              </span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Consulenza strategica di alto livello per chi decide gli
              investimenti sportivi della propria azienda. Costruiamo business
              case, framework di misurazione e roadmap operative che parlano
              la lingua dei C-level e si difendono in qualunque board.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — CEO, CMO, CFO, CSR Director</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Durata — Da 4 settimane a 12 mesi</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Business case, framework, advisory continuativa</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Gli investimenti sportivi vengono ancora difesi con
                  argomenti del 1990.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Quando un'azienda investe in una sponsorship, in una
                  partnership sportiva o in un programma di brand activation
                  legato allo sport, deve giustificare la spesa al board. La
                  cosa più assurda del settore è che, nel 2026, la
                  maggioranza di queste giustificazioni continua a essere
                  costruita con KPI che non significano nulla per chi decide
                  davvero — reach potenziale, GRP, impressions, ore di
                  visibilità a bordo campo. Sono indicatori di esposizione,
                  non di valore.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il risultato è prevedibile. Quando il CFO o il board
                  chiedono "che cosa abbiamo ottenuto", il marketing
                  risponde con un dashboard di numeri grossi ma scollegati
                  dal P&amp;L. Quando il budget si stringe — e prima o poi
                  succede a tutti — lo sport diventa la prima voce a essere
                  tagliata, perché è quella su cui nessuno sa davvero
                  spiegare il ritorno.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Il problema non è lo sport. Il problema è che lo sport
                  viene gestito nelle aziende italiane come un'attività di
                  "comunicazione" anziché come un investimento strategico
                  misurabile. Non c'è un framework. Non c'è un business
                  case. Non c'è una governance. Senza questi tre elementi,
                  qualsiasi investimento sportivo resta esposto al rischio
                  politico interno dell'azienda.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FACCIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-cosa-facciamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Cosa facciamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Trasformiamo lo sport in una strategia difendibile.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Lavoriamo con C-level, responsabili marketing e CSR
                  manager che hanno bisogno di costruire — o ricostruire — la
                  propria strategia sportiva su basi solide. Partiamo dal
                  posizionamento del brand, dagli obiettivi di business e
                  dalle ambizioni ESG dichiarate. Da lì costruiamo il
                  framework che lega gli investimenti sportivi a risultati
                  tracciabili su tre piani: Business Impact (ricavi, lead,
                  conversioni), Cultural Impact (percezione, narrazione,
                  autorità), Social Impact (impatto sociale documentabile).
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il nostro lavoro è metodico, non creativo. Non vendiamo
                  idee — vendiamo strutture decisionali. Un business case
                  dell'Impact Strategy di OnSports è un documento che il
                  CFO può leggere, capire e portare in board. Una roadmap è
                  un piano operativo trimestrale con KPI dichiarati, non un
                  piano editoriale. Un framework di misurazione è una
                  scorecard che funziona anche quando noi non ci siamo più.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Il servizio si declina in tre formati principali:
                  assessment puntuale (4-6 settimane, output: business case +
                  framework), roadmap strategica (8-12 settimane, output:
                  piano operativo annuale + KPI), advisory continuativa (12
                  mesi+, output: presidio strategico ricorrente per il team
                  del cliente).
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL METODO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-metodo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Il metodo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Cinque passi. Sotto un'unica regia.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {METHOD_STEPS.map((step, i) => (
              <RevealOnScroll key={step.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-dodgers/[0.02] h-full flex flex-col"
                  data-testid={`method-step-${step.num}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-lime text-3xl md:text-4xl leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {step.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 5 — COSA INCLUDIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-includiamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa includiamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">I deliverable che porti via.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="deliverables-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Output</th>
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Formato</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Destinatario</th>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map((d, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top">{d.output}</td>
                      <td className="py-4 pr-6 align-top opacity-80">{d.format}</td>
                      <td className="py-4 align-top opacity-80">{d.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — PER CHI È */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre tipologie di cliente.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CASI DI RIFERIMENTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-casi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Casi di riferimento</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Strategia, applicata.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {REFERENCE_CASES.map((c, i) => (
              <RevealOnScroll key={c.to} delay={i * 0.06}>
                <Link
                  to={c.to}
                  className="group block focus:outline-none"
                  data-hover="true"
                  data-testid={`case-card-${i}`}
                >
                  <div className="overflow-hidden bg-galactico/[0.05]" style={{ aspectRatio: "4 / 3" }}>
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                      draggable="false"
                    />
                  </div>
                  <span className="block mt-4 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    ● {c.chip}
                  </span>
                  <h3 className="mt-2 font-display text-xl md:text-2xl leading-tight group-hover:text-lime transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.55] opacity-85">{c.kpi}</p>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 8 — MISURAZIONE & KPI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-kpi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Misurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre piani. Sempre.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="kpi-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Dimensione</th>
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">KPI tipici</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Strumenti</th>
                  </tr>
                </thead>
                <tbody>
                  {KPI_3D.map((k, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top font-display text-base md:text-lg">{k.dim}</td>
                      <td className="py-4 pr-6 align-top opacity-90 max-w-[40ch]">{k.kpi}</td>
                      <td className="py-4 align-top opacity-80">{k.tools}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — FAQ */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-galactico/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Lavoriamo insieme alla tua strategia di <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              Una call di 30 minuti per capire se siamo gli interlocutori giusti.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta consulenza — Impact Strategy & Advisory"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioImpactStrategy;
